import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { PageHero, SectionWrapper, SectionHeader } from "../../components/Section";

/**
 * Reusable layout for an individual Allinstra solution sub-page.
 *
 * props:
 *   icon          : lucide icon component
 *   overline      : "Solution · X of 7" style overline
 *   title         : page title
 *   headline      : large supporting headline (string OR JSX)
 *   intro         : array of paragraphs (strings)
 *   supports      : array of "what this solution supports" bullets
 *   modules       : array of "Relevant module areas may include" items
 *   note          : "Important note" paragraph
 *   ctaTitle      : final CTA heading
 *   primaryCta    : { label, to }  // typically -> /contact
 *   secondaryCta  : { label, to }  // typically -> /product-portfolio
 *   testId        : page test id (e.g. "page-solution-municipality")
 */
const SolutionPageLayout = ({
  icon: Icon,
  overline,
  title,
  headline,
  intro = [],
  supports = [],
  supportsOverline = "What this solution supports",
  supportsTitle = "Operational scope at a glance.",
  modules = [],
  note,
  ctaTitle,
  primaryCta,
  secondaryCta,
  testId,
}) => (
  <main data-testid={testId}>
    <PageHero overline={overline} title={title} lead={headline}>
      <div className="flex flex-wrap gap-3">
        <Link
          to={primaryCta?.to || "/contact"}
          data-testid={`${testId}-hero-primary`}
          className="btn-primary"
        >
          {primaryCta?.label || "Discuss Your Institution"} <ArrowUpRight size={16} />
        </Link>
        <Link
          to={secondaryCta?.to || "/product-portfolio"}
          data-testid={`${testId}-hero-secondary`}
          className="btn-secondary"
        >
          {secondaryCta?.label || "View Product Portfolio"} <ArrowRight size={14} />
        </Link>
      </div>
    </PageHero>

    {intro.length > 0 && (
      <SectionWrapper id="solution-intro">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            {Icon && (
              <div className="w-14 h-14 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
                <Icon size={22} strokeWidth={1.5} />
              </div>
            )}
            <div className="tag-overline mt-6">Operational context</div>
          </div>
          <div className="lg:col-span-8 space-y-5 text-[16px] leading-relaxed text-ink-700">
            {intro.map((p, i) => (
              <p key={i} className={i === intro.length - 1 ? "text-ink-900" : ""}>
                {p}
              </p>
            ))}
          </div>
        </div>
      </SectionWrapper>
    )}

    {supports.length > 0 && (
      <SectionWrapper id="solution-supports" alt>
        <SectionHeader
          overline={supportsOverline}
          title={supportsTitle}
        />
        <div
          className={`mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 ${
            supports.length % 3 === 0 ? "lg:grid-cols-3" : ""
          }`}
        >
          {supports.map((s, i) => (
            <div
              key={i}
              data-testid={`solution-support-${i + 1}`}
              className="bg-white border border-ink-200 p-5 sm:p-6 flex items-start gap-3 card-hover"
            >
              <Check size={16} className="text-cyan-600 mt-0.5 shrink-0" strokeWidth={2} />
              <span className="text-[14.5px] text-ink-800 leading-relaxed">{s}</span>
            </div>
          ))}
        </div>
      </SectionWrapper>
    )}

    {modules.length > 0 && (
      <SectionWrapper id="solution-modules">
        <SectionHeader
          overline="Relevant module areas may include"
          title="Module areas that can be considered for this solution."
          lead="Module availability and scope are determined per institution, depending on local legislation, public infrastructure, third-party access, and implementation feasibility."
        />
        <div className="mt-10 border border-ink-200 bg-white">
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {modules.map((m, i) => {
              const cols = 3;
              const isLastRow = i >= modules.length - (modules.length % cols || cols);
              const isLastCol = (i + 1) % cols === 0;
              return (
                <li
                  key={i}
                  data-testid={`solution-module-${i + 1}`}
                  className={`flex items-start gap-3 px-5 py-3.5 text-[14px] text-ink-800 border-ink-200 ${
                    !isLastCol ? "lg:border-r" : ""
                  } ${(i + 1) % 2 === 1 ? "sm:border-r lg:border-r" : ""} ${
                    !isLastRow ? "border-b" : ""
                  }`}
                >
                  <span
                    className="mt-1 w-1.5 h-1.5 bg-cyan-500 shrink-0"
                    aria-hidden="true"
                  />
                  <span>{m}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </SectionWrapper>
    )}

    {note && (
      <SectionWrapper id="solution-note" alt>
        <div className="border-l-2 border-cyan-500 pl-6 max-w-3xl">
          <div className="tag-overline">Important note</div>
          <p className="mt-3 text-[15.5px] text-ink-700 leading-relaxed">{note}</p>
        </div>
      </SectionWrapper>
    )}

    <SectionWrapper id="solution-cta">
      <div className="border border-ink-200 p-8 sm:p-12 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="tag-overline">Next step</div>
            <h2 className="heading-display mt-3 text-2xl sm:text-3xl font-semibold">
              {ctaTitle}
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
            <Link
              to={primaryCta?.to || "/contact"}
              data-testid={`${testId}-cta-primary`}
              className="btn-primary"
            >
              {primaryCta?.label || "Contact Us"} <ArrowUpRight size={16} />
            </Link>
            <Link
              to={secondaryCta?.to || "/product-portfolio"}
              data-testid={`${testId}-cta-secondary`}
              className="btn-secondary"
            >
              {secondaryCta?.label || "View Product Portfolio"}
            </Link>
          </div>
        </div>
      </div>
    </SectionWrapper>
  </main>
);

export default SolutionPageLayout;
